import { useEffect, useMemo, useRef, useState } from 'react';
import { CloudUpload, Save, ShieldCheck } from 'lucide-react';
import type { AdminApi, Environment, LiveConfig, Platform, SocialRewardConfig } from '../api';
import { ClientReleaseSelect, ConfigIssues } from '../components/RewardConfigControls';
import { useClientRelease } from '../hooks/useClientRelease';

interface Props { api: AdminApi; gameId: string; environment: Environment; platform: Platform; notify: (message: string) => void }
type RewardKey = 'gameClubDailyReward' | 'favoriteReward';
const PLATFORM_LABELS = { wechat: '微信', douyin: '抖音', taptap: 'TapTap' } as const;

export function SocialRewardsCenter(props: Props) {
  const [config, setConfig] = useState<LiveConfig | null>(null);
  const [activeVersion, setActiveVersion] = useState('未发布');
  const [loadedReleaseKey, setLoadedReleaseKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'draft' | 'publish' | ''>('');
  const [dirty, setDirty] = useState(false);
  const [serverIssue, setServerIssue] = useState('');
  const sequence = useRef(0);
  const { releaseKey, releaseKeys, releaseError, retryReleases, setReleaseKey } = useClientRelease(props.api, props.gameId, props.environment);

  const load = async () => {
    if (!releaseKey) return;
    const current = ++sequence.current;
    setLoading(true); setServerIssue('');
    try {
      const [versions, template] = await Promise.all([
        props.api.versions(props.gameId, props.environment, releaseKey),
        props.api.configTemplate(props.gameId),
      ]);
      if (current !== sequence.current) return;
      const source = versions.active?.payload ?? versions.versions[0]?.payload ?? template.payload;
      const { config: next, upgraded } = hydrateSocialRewards(source, template.payload);
      next.releaseVersion = nextRelease(next.releaseVersion);
      setConfig(next); setActiveVersion(versions.active?.releaseVersion ?? '未发布');
      setLoadedReleaseKey(releaseKey); setDirty(upgraded);
    } catch (error) {
      if (current !== sequence.current) return;
      const message = error instanceof Error ? error.message : '社交奖励配置读取失败';
      setServerIssue(message); props.notify(message);
    } finally { if (current === sequence.current) setLoading(false); }
  };
  useEffect(() => { void load(); return () => { sequence.current += 1; }; }, [props.gameId, props.environment, releaseKey]);

  const issues = useMemo(() => validate(config), [config]);
  const update = (key: RewardKey, mutate: (reward: SocialRewardConfig) => void) => {
    setConfig(current => {
      if (!current) return current;
      const next = structuredClone(current); const reward = next.activities[key];
      if (reward) mutate(reward); return next;
    });
    setDirty(true); setServerIssue('');
  };
  const save = async (publish: boolean) => {
    if (!config || busy || issues.length || !releaseKey || loadedReleaseKey !== releaseKey) return;
    setBusy(publish ? 'publish' : 'draft'); setServerIssue('');
    try {
      const payload = structuredClone(config);
      const result = await props.api.createDraft(props.gameId, `社交奖励 · ${payload.releaseVersion}`, props.environment, payload);
      if (!result.draft.validation.valid) throw new Error(`服务端校验未通过：${result.draft.validation.issues.length} 项问题`);
      if (publish) {
        await props.api.publishDraft(props.gameId, result.draft.draftId, true, releaseKey);
        setActiveVersion(payload.releaseVersion); setConfig({ ...payload, releaseVersion: nextRelease(payload.releaseVersion) });
      }
      setDirty(false); props.notify(publish ? '社交奖励配置已发布' : '社交奖励草稿已保存并通过校验');
    } catch (error) {
      const message = error instanceof Error ? error.message : '社交奖励配置保存失败';
      setServerIssue(message); props.notify(message);
    } finally { setBusy(''); }
  };

  if (releaseError) return <div className="page"><div className="empty-state" role="alert">{releaseError}<button className="secondary-button" onClick={retryReleases}>重新读取版本</button></div></div>;
  if (!releaseKey || loading || loadedReleaseKey !== releaseKey) return <div className="page"><div className="loading-card">正在读取社交奖励配置…</div></div>;
  if (!config?.activities.gameClubDailyReward || !config.activities.favoriteReward) return <div className="page"><div className="empty-state">社交奖励默认配置加载失败，请刷新后重试。</div></div>;
  return <div className="page social-rewards-page">
    <section className="page-intro"><div><p className="eyebrow">SOCIAL REWARD CONTRACT</p><h2>社交奖励</h2><span>游戏圈与桌面添加奖励独立控制；奖励由服务端幂等发放。</span></div><div className="intro-actions">
      <ClientReleaseSelect value={releaseKey} options={releaseKeys} disabled={Boolean(busy)} onChange={value => { if (!dirty || window.confirm('切换版本会放弃当前未保存改动，确定切换吗？')) { setLoading(true); setReleaseKey(value); } }} />
      <span className={`draft-state ${dirty ? 'dirty' : ''}`}>{dirty ? '有未保存改动' : `当前 ${activeVersion}`}</span>
      <button className="secondary-button" disabled={Boolean(busy) || issues.length > 0} onClick={() => void save(false)}><Save size={16}/>{busy === 'draft' ? '保存中…' : '保存草稿'}</button>
      <button className="primary-button compact" disabled={Boolean(busy) || issues.length > 0} onClick={() => void save(true)}><CloudUpload size={16}/>{busy === 'publish' ? '发布中…' : '发布并激活'}</button>
    </div></section>
    <ConfigIssues issues={issues} serverIssue={serverIssue}/>
    <div className="social-reward-grid">
      <RewardCard title="游戏圈每日奖励" cadence="每日（上海自然日）" reward={config.activities.gameClubDailyReward} onChange={fn => update('gameClubDailyReward', fn)}/>
      <RewardCard title="桌面添加一次性奖励" cadence="从安卓桌面进入后永久一次" reward={config.activities.favoriteReward} onChange={fn => update('favoriteReward', fn)}/>
    </div>
    <div className="compliance-banner"><ShieldCheck size={20}/><div><strong>平台边界</strong><span>仅安卓微信客户端识别场景值 1023；该场景值来自客户端启动信息，不是服务端可独立验签的桌面凭证。未接入的平台即使误开也不会发奖。</span></div></div>
  </div>;
}

function RewardCard({ title, cadence, reward, onChange }: { title: string; cadence: string; reward: SocialRewardConfig; onChange: (fn: (reward: SocialRewardConfig) => void) => void }) {
  const fish = reward.reward.items.find(item => item.type === 'fish_dry');
  return <section className="panel social-reward-card"><header><div><h3>{title}</h3><p>{reward.activityId} · {reward.configVersion}</p></div><label className="switch"><input type="checkbox" checked={reward.enabled} onChange={event => onChange(draft => { draft.enabled = event.target.checked; })}/><span/></label></header>
    <div className="social-reward-form"><label>领取周期<input value={cadence} disabled/></label><label>鱼干奖励<input type="number" min={1} max={1000000} value={fish?.type === 'fish_dry' ? fish.amount : 0} onChange={event => onChange(draft => { draft.reward = { items: [{ type: 'fish_dry', amount: Number(event.target.value) }] }; })}/></label><label>首页功能<select value={reward.requiredFeatureId} onChange={event => onChange(draft => { draft.requiredFeatureId = event.target.value; })}><option value="game_club">游戏圈</option><option value="favorite_reward">桌面有礼</option></select></label><label>最低客户端版本<input value={reward.minClientVersion} onChange={event => onChange(draft => { draft.minClientVersion = event.target.value; })}/></label></div>
    <div className="social-platforms">{(Object.keys(PLATFORM_LABELS) as Array<keyof typeof PLATFORM_LABELS>).map(platform => <label key={platform}><input type="checkbox" checked={reward.platforms[platform]} onChange={event => onChange(draft => { draft.platforms[platform] = event.target.checked; })}/><span>{PLATFORM_LABELS[platform]}</span></label>)}</div>
  </section>;
}

function validate(config: LiveConfig | null): string[] {
  if (!config) return ['配置尚未加载'];
  const issues: string[] = [];
  (['gameClubDailyReward', 'favoriteReward'] as RewardKey[]).forEach(key => {
    const value = config.activities[key];
    if (!value) { issues.push(`缺少 ${key}`); return; }
    const fish = value.reward.items.find(item => item.type === 'fish_dry');
    const amount = fish && 'amount' in fish ? fish.amount : 0;
    if (!Number.isInteger(amount) || amount < 1) issues.push(`${value.displayName}必须配置正整数鱼干奖励`);
    if (value.enabled && !Object.values(value.platforms).some(Boolean)) issues.push(`${value.displayName}启用时至少打开一个平台`);
    if (!config.homeFeatures.some(feature => feature.featureId === value.requiredFeatureId)) issues.push(`${value.displayName}引用了未知首页功能`);
    if (!/^\d+\.\d+\.\d+/.test(value.minClientVersion)) issues.push(`${value.displayName}最低客户端版本格式无效`);
  });
  return issues;
}

function hydrateSocialRewards(source: LiveConfig, template: LiveConfig): { config: LiveConfig; upgraded: boolean } {
  const config = structuredClone(source);
  let upgraded = false;
  (['gameClubDailyReward', 'favoriteReward'] as RewardKey[]).forEach(key => {
    if (!config.activities[key] && template.activities[key]) {
      config.activities[key] = structuredClone(template.activities[key]);
      upgraded = true;
    }
  });
  const desktopReward = config.activities.favoriteReward;
  if (desktopReward && desktopReward.displayName !== '桌面添加一次性奖励') {
    desktopReward.displayName = '桌面添加一次性奖励';
    upgraded = true;
  }
  const desktopFeature = config.homeFeatures.find(feature => feature.featureId === 'favorite_reward');
  if (desktopFeature && desktopFeature.displayName !== '桌面有礼') {
    desktopFeature.displayName = '桌面有礼';
    upgraded = true;
  }
  ['game_club', 'favorite_reward'].forEach(featureId => {
    if (config.homeFeatures.some(feature => feature.featureId === featureId)) return;
    const feature = template.homeFeatures.find(candidate => candidate.featureId === featureId);
    if (feature) {
      config.homeFeatures.push(structuredClone(feature));
      upgraded = true;
    }
  });
  return { config, upgraded };
}
function nextRelease(value: string) { const match = value.match(/^(.*?)(\d+)$/); return match ? `${match[1]}${Number(match[2]) + 1}` : `${value}-next`; }
